/* eslint-disable max-len */
import React, {
    useState, useEffect, useMemo, ChangeEvent,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { UncontrolledTooltip } from 'reactstrap';
import { IOptionAnyValue } from 'bricks/types';
import { IncomeCalculationType } from 'constants/proForma';
import { NSBadge, NSCellInput, NSSelect } from 'bricks';
import { customStyles } from 'bricks/NSSelect/NSSelect';
import { IPatchOperatingInfoData, IPeriod } from 'views/ProFormaTable/types';
import RenderIf from '../../../../../../../components/RenderIf/RenderIf';
import { OPERATIONS_INCOME_WS_HANDLERS, CALCULATION_OPTIONS } from '../../../../../constants';
import ProFormaGenericTable from '../../../../ProFormaGenericTable/ProFormaGenericTable';
import { ProFormaGenericRow, ProFormaGenericColumn } from '../../../../ProFormaGenericTable/types';
import PeriodsSelect from '../../../ProFormaDevelopment/PeriodsSelect/PeriodsSelect';
import { useFloorAllocations } from './hooks/useFloorAllocations';
import '../ProFormaIncomeTable.scss';
import {
    formatCurrencyWithParentheses,
    getTenantPeriods,
    shouldShowCents,
    getEstimateRentalPeriods,
    getPrimitivePeriods,
    getOtherIncomePeriods,
    getPeriods,
} from '../../../../../helpers';
import {
    BASE_ACTUAL_RENTAL_COLUMNS,
    BASE_ACTUAL_RECOVERY_COLUMNS,
    OTHER_INCOME_BASE_COLUMNS,
    TOTAL_POTENTIAL_BASE_ACTUALS_COLUMNS,
    RENT_STEP_OPTIONS,
    BASE_RENTAL_COLUMNS,
    BASE_RECOVERY_COLUMNS,
} from './constants';
import { useProFormaSocketContext } from '../../../socketContext/ProFormaSocketProvider';
import { useCustomScroll } from '../../../../../hooks/useCustomScroll';

export const OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS = [
    'office-industrial-retail-rental',
    'office-industrial-retail-recovery',
    'office-industrial-retail-other-income',
    'office-industrial-retail-total-potential',
    'office-industrial-retail-income-adjustments',
] as const;

const OfficeIndustrialRetailIncomeTable = () => {
    const {
        incomeSummary,
        handleCreateOperationsRow,
        handleUpdateOperationsRow,
        handleDeleteOperationsRow,
        handleUpdateProFormaOperations,
        proForma,
        cashFlowPeriods,
        operatingInfo,
        showCents,
        operatingPeriodCashFlows,
    } = useProFormaSocketContext();

    const [rentalRows, setRentalRows] = useState<ProFormaGenericRow[]>([]);
    const [recoveryRows, setRecoveryRows] = useState<ProFormaGenericRow[]>([]);
    const [estimateRentalRows, setEstimateRentalRows] = useState<ProFormaGenericRow[]>([]);
    const [estimateRecoveryRows, setEstimateRecoveryRows] = useState<ProFormaGenericRow[]>([]);
    const [otherIncomeRows, setOtherIncomeRows] = useState<ProFormaGenericRow[]>([]);
    const [incomeAdjustmentsRows, setIncomeAdjustmentsRows] = useState<ProFormaGenericRow[]>([]);

    const { incompleteAllocationFloors, isLastRowOfFloor, hasAllFloors } = useFloorAllocations(incomeSummary?.incomeFloorEstimates, proForma?.averageStories);

    const {
        tableRef: rentalTableRef,
        handleTableScroll: handleRentalTableScroll,
        createRefHandler: createRentalRefHandler,
    } = useCustomScroll(OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS[0], OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS.filter(id => id !== OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS[0]));

    const {
        tableRef: recoveryTableRef,
        handleTableScroll: handleRecoveryTableScroll,
        createRefHandler: createRecoveryRefHandler,
    } = useCustomScroll(OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS[1], OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS.filter(id => id !== OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS[1]));

    const {
        tableRef: otherIncomeTableRef,
        handleTableScroll: handleOtherIncomeTableScroll,
        createRefHandler: createOtherIncomeRefHandler,
    } = useCustomScroll(OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS[2], OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS.filter(id => id !== OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS[2]));

    const {
        tableRef: totalPotentialTableRef,
        handleTableScroll: handleTotalPotentialTableScroll,
        createRefHandler: createTotalPotentialRefHandler,
    } = useCustomScroll(OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS[3], OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS.filter(id => id !== OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS[3]));

    const {
        tableRef: incomeAdjustmentsTableRef,
        handleTableScroll: handleIncomeAdjustmentsTableScroll,
        createRefHandler: createIncomeAdjustmentsRefHandler,
    } = useCustomScroll(OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS[4], OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS.filter(id => id !== OFFICE_INDUSTRIAL_RETAIL_SYNC_TABLE_IDS[4]));

    useEffect(() => {
        if (!cashFlowPeriods?.length) return undefined;

        requestAnimationFrame(() => {
            if (operatingInfo?.operationBeginMonth !== undefined && rentalTableRef.current) {
                const scrollIndex = operatingInfo.operationBeginMonth - 1;
                if (scrollIndex >= 0) {
                    const scrollAmount = scrollIndex * 130;
                    rentalTableRef.current.scrollTo({ left: scrollAmount, behavior: 'smooth' });
                }
            }
        });
        return undefined;
    }, [operatingInfo?.operationBeginMonth, cashFlowPeriods, rentalTableRef]);

    const isIncomeCalculationTypeTenants = operatingInfo?.incomeCalculationType === IncomeCalculationType.TENANTS;

    const filteredCalculationOptions = useMemo(() => {
        if (!incomeSummary?.incomeAdjustmentCalculationTypes) return CALCULATION_OPTIONS;
        return CALCULATION_OPTIONS.filter(option => incomeSummary.incomeAdjustmentCalculationTypes.includes(option.value));
    }, [incomeSummary?.incomeAdjustmentCalculationTypes]);

    const handleSelectCalculation = (rowId: string) => (option: IOptionAnyValue | null) => {
        if (option) {
            setIncomeAdjustmentsRows(rows => rows.map(row => (
                row?.id === rowId ? { ...row, selectedCalculation: option } : row
            )));

            const updateFn = handleUpdateOperationsRow(
                OPERATIONS_INCOME_WS_HANDLERS.INCOME_ADJUSTMENT.UPDATE,
                Number(rowId),
                'calculationType',
            );
            if (typeof updateFn === 'function') {
                updateFn({
                    target: { value: option.value },
                } as React.ChangeEvent<HTMLInputElement>);
            }
        }
    };

    const handleUpdateEstimateRecovery = (rowId: number, dataKey: keyof IPatchOperatingInfoData) => (e: React.ChangeEvent<HTMLInputElement>) => {
        handleUpdateProFormaOperations(operatingInfo?.id, {
            [dataKey]: e.target.value,
        });
    };

    const isVacancyOrCreditLoss = (row: ProFormaGenericRow) => row?.name === 'Vacancy' || row?.name === 'Credit loss';
    const tenantPeriods = useMemo(() => {
        const rentStartDates = (incomeSummary?.incomeTenants || []).reduce<Record<string, number>>((acc, tenant) => ({
            ...acc,
            [tenant?.tenant || '']: tenant?.rentStartMonth || -1,
        }), {});

        return getTenantPeriods(
            cashFlowPeriods,
            operatingPeriodCashFlows,
            incomeSummary?.incomeTenants?.map(tenant => ({
                name: tenant?.tenant || '',
            })) || [],
            false,
            rentStartDates,
            'incomeTenants',
        );
    }, [cashFlowPeriods, operatingPeriodCashFlows, incomeSummary?.incomeTenants, operatingInfo?.operationBeginMonth]);

    const recoveryPeriods = useMemo(() => {
        const recoveryStartDates = (incomeSummary?.tenantRecoveries || []).reduce<Record<string, number>>((acc, recovery) => {
            const startDate = recovery?.startMonth || -1;
            return {
                ...acc,
                [recovery?.tenant || '']: startDate,
            };
        }, {});

        const periods = getTenantPeriods(
            cashFlowPeriods,
            operatingPeriodCashFlows,
            incomeSummary?.tenantRecoveries?.map(recovery => ({
                name: recovery?.tenant || '',
            })) || [],
            false,
            recoveryStartDates,
            'tenantRecoveries',
        );

        return periods;
    }, [cashFlowPeriods, operatingPeriodCashFlows, incomeSummary?.tenantRecoveries, operatingInfo?.operationBeginMonth]);

    const estimateRentalPeriods = useMemo(() => getEstimateRentalPeriods(
        cashFlowPeriods,
        operatingPeriodCashFlows,
        incomeSummary?.incomeFloorEstimates || [],
        operatingInfo?.operationBeginMonth,
        false,
    ), [cashFlowPeriods, operatingPeriodCashFlows, incomeSummary?.incomeFloorEstimates, operatingInfo?.operationBeginMonth]);

    const estimateRecoveryPeriods = useMemo(() => getPrimitivePeriods(
        cashFlowPeriods,
        operatingPeriodCashFlows,
        'recoveryEstimate',
        operatingInfo?.operationBeginMonth,
        false,
    ), [cashFlowPeriods, operatingPeriodCashFlows, operatingInfo?.operationBeginMonth]);

    const incomeAdjustmentPeriods = useMemo(() => getPeriods(
        cashFlowPeriods,
        operatingPeriodCashFlows,
        incomeSummary?.incomeAdjustments || [],
        'incomeAdjustments',
        operatingInfo?.operationBeginMonth,
        !operatingInfo?.operationBeginMonth,
    ), [proForma, cashFlowPeriods, operatingPeriodCashFlows, incomeSummary?.incomeAdjustments]);

    const createPeriodColumns = (
        periods: IPeriod[],
        format: ProFormaGenericColumn['format'] = 'currency',
        showCents: boolean = false,
        getHeaderValue?: (period: IPeriod) => number | string,
    ): ProFormaGenericColumn[] => periods.map((period, index) => ({
        key: `column_${period.periodDate}`,
        field: `column_${period.periodDate}`,
        header: React.createElement(
            'div',
            null,
            React.createElement(
                'div',
                { className: 'h6 NSTable__thead__tr__th--grey-text text-uppercase text-nowrap text-secondary text-right mb-0' },
                'MONTH',
                ' ',
                index,
            ),
            React.createElement(
                'div',
                { className: 'h6 text-uppercase text-right text-nowrap mt-1' },
                getHeaderValue
                    ? formatCurrencyWithParentheses(Number(getHeaderValue(period)), shouldShowCents(`column_${period.periodDate}`, showCents))
                    : period.periodName,
            ),
        ),
        format,
        isFixed: false,
    }));

    const incomeAdjustmentsColumns: ProFormaGenericColumn[] = [
        {
            key: 'name',
            field: 'name',
            header: 'Income adjustments',
            format: 'none',
            type: 'text',
            isFixed: true,
            isReadOnly: isVacancyOrCreditLoss,
            dropdown: {
                label: 'From:',
                options: filteredCalculationOptions,
                getSelectedOption: (row: ProFormaGenericRow) => {
                    const adjustment = incomeSummary?.incomeAdjustments?.find(adj => String(adj?.id) === row.id);
                    if (!adjustment) return undefined;
                    return filteredCalculationOptions.find(opt => opt.value === adjustment.calculationType);
                },
                handleSelectOption: handleSelectCalculation,
                shouldShowDropdown: () => true,
            },
        },
        {
            key: 'percentOfIncome',
            field: 'percentOfIncome',
            header: '% of income',
            format: 'percentage',
            isFixed: true,
        },
        {
            key: 'pricePerSfPerMonth',
            field: 'pricePerSfPerMonth',
            header: isIncomeCalculationTypeTenants ? '$/RSF/mo.' : '$/SF/mo.',
            format: 'currency',
            isFixed: true,
            isReadOnly: true,
        },
        {
            key: 'amountPerYear',
            field: 'amountPerYear',
            header: 'Amount/year',
            format: 'currency',
            isFixed: true,
            isReadOnly: true,
        },
        ...createPeriodColumns(incomeAdjustmentPeriods),
    ];

    const rentalEstimateColumns = useMemo(() => {
        const columns = [...BASE_RENTAL_COLUMNS];
        const floorColumn = columns.find(col => col.key === 'floor');
        const allocationColumn = columns.find(col => col.key === 'allocationPercentage');

        if (allocationColumn) {
            allocationColumn.format = 'none';
            allocationColumn.type = 'text';
            allocationColumn.isReadOnly = true;
            allocationColumn.customCell = {
                isDeletable: true,
                shouldShowCustomCell: () => true,
                renderCell: ({ onChange, row, value }) => (
                    <div className="d-flex align-items-center m-2">
                        <NSCellInput
                            type="number"
                            numberType="percentage"
                            value={value || 0}
                            handleUpdateValue={updatedValue => onChange({ target: { value: updatedValue } } as ChangeEvent<HTMLInputElement>)}
                            placeholder="Enter percentage"
                            isRightAligned
                            inputHeight="26px"
                            inputWidth="54px"
                            customClassName="m-2"
                        />
                        <RenderIf isTrue={incompleteAllocationFloors.includes(Number(row.floor)) && isLastRowOfFloor(row?.id, Number(row?.floor))}>
                            <UncontrolledTooltip target="wrong-allocation"> Total allocation for a floor must equal 100%.</UncontrolledTooltip>
                            <NSBadge
                                id="wrong-allocation"
                                className="ml-1"
                                color="warning-lighten"
                            >
                                <FontAwesomeIcon
                                    icon={faTriangleExclamation}
                                    className="mr-1"
                                />
                                Allocation
                                {' '}
                                {'<'}
                                {' '}
                                100%
                            </NSBadge>
                        </RenderIf>
                    </div>
                ),
            };
        }

        if (floorColumn) {
            floorColumn.customBorderClassname = row => {
                const floor = Number(row.floor);
                if (!incompleteAllocationFloors.includes(floor)) return '';
                return isLastRowOfFloor(row?.id, floor) ? 'ProFormaGenericTable__border-warning' : '';
            };
        }

        return [...columns, ...createPeriodColumns(estimateRentalPeriods)];
    }, [estimateRentalPeriods, incompleteAllocationFloors, isLastRowOfFloor]);

    const rentalColumns = useMemo(() => {
        const columns = [...BASE_ACTUAL_RENTAL_COLUMNS];
        const leaseStartColumn = columns.find(col => col.key === 'leaseStart');
        const rentStartColumn = columns.find(col => col.key === 'rentStart');
        const rentStepColumn = columns.find(col => col.key === 'rentStep');

        if (leaseStartColumn) {
            leaseStartColumn.format = 'none';
            leaseStartColumn.type = 'text';
            leaseStartColumn.isReadOnly = true;
            leaseStartColumn.customCell = {
                shouldShowCustomCell: () => true,
                renderCell: ({ onChange, row }) => {
                    const tenant = incomeSummary?.incomeTenants?.find(t => String(t.id) === row.id);
                    return (
                        <PeriodsSelect
                            compact
                            hasInvertedColors
                            isStartDate
                            budgetLineEndDate={null}
                            budgetLineStartDate={tenant?.leaseStart || ''}
                            updateBudgetClassification={(dict: { startDate?: string }) => {
                                onChange({
                                    target: { value: dict.startDate ?? '' },
                                } as ChangeEvent<HTMLInputElement>);
                            }}
                            cashFlowPeriods={cashFlowPeriods || []}
                            saleDate={proForma?.saleDate || ''}
                            disabled={!operatingInfo?.operationStartDate || !proForma?.saleDate}
                            operationStartDate={operatingInfo?.operationStartDate || ''}
                        />
                    );
                },
            };
        }

        if (rentStartColumn) {
            rentStartColumn.format = 'none';
            rentStartColumn.type = 'text';
            rentStartColumn.isReadOnly = true;
            rentStartColumn.customCell = {
                shouldShowCustomCell: () => true,
                renderCell: ({ onChange, row }) => {
                    const tenant = incomeSummary?.incomeTenants?.find(t => String(t.id) === row.id);
                    return (
                        <PeriodsSelect
                            compact
                            hasInvertedColors
                            isStartDate
                            budgetLineEndDate={null}
                            budgetLineStartDate={tenant?.rentStart || ''}
                            updateBudgetClassification={(dict: { startDate?: string }) => {
                                onChange({
                                    target: { value: dict.startDate ?? '' },
                                } as ChangeEvent<HTMLInputElement>);
                            }}
                            cashFlowPeriods={cashFlowPeriods || []}
                            saleDate={proForma?.saleDate || ''}
                            disabled={!operatingInfo?.operationStartDate || !proForma?.saleDate}
                            operationStartDate={operatingInfo?.operationStartDate || ''}
                        />
                    );
                },
            };
        }

        if (rentStepColumn) {
            rentStepColumn.format = 'none';
            rentStepColumn.type = 'text';
            rentStepColumn.isReadOnly = true;
            rentStepColumn.customCell = {
                shouldShowCustomCell: () => true,
                renderCell: ({ row, onChange }) => (
                    <div className="d-flex justify-content-end p-0 m-0">
                        <NSSelect
                            name={`${row.id}-rentStep-select`}
                            options={RENT_STEP_OPTIONS}
                            value={RENT_STEP_OPTIONS.find(opt => opt.value === Number(row.rentStep))}
                            onChange={(option: any) => {
                                if (option) {
                                    const input = document.createElement('input');
                                    input.value = option.value;
                                    onChange({ target: input } as ChangeEvent<HTMLInputElement>);
                                }
                            }}
                            isClearable={false}
                            menuPosition="fixed"
                            menuPortalTarget={document.body}
                            small
                            styles={{
                                ...customStyles,
                                control: (provided: any) => ({
                                    ...provided,
                                    backgroundColor: '#4F5B69',
                                    width: '100%',
                                    border: 'none',
                                    minHeight: '28px',
                                    paddingRight: '4px',
                                }),
                                singleValue: (provided: any) => ({
                                    ...provided,
                                    color: '#e3eaef',
                                    paddingRight: '4px',
                                    overflow: 'visible',
                                }),
                                menuList: (provided: any) => ({
                                    ...provided,
                                    width: '100px',
                                }),
                            }}
                        />
                    </div>
                ),
            };
        }

        return [...columns, ...createPeriodColumns(tenantPeriods)];
    }, [cashFlowPeriods, proForma?.saleDate, operatingInfo?.operationStartDate, tenantPeriods, incomeSummary?.incomeTenants]);

    const estimateRecoveryColumns = useMemo(() => {
        const columns = [...BASE_RECOVERY_COLUMNS];
        const recoveryStartColumn = columns.find(col => col.key === 'recoveryStart');

        if (recoveryStartColumn) {
            recoveryStartColumn.format = 'none';
            recoveryStartColumn.type = 'text';
            recoveryStartColumn.isReadOnly = true;
            recoveryStartColumn.customCell = {
                shouldShowCustomCell: () => true,
                renderCell: ({ row, onChange, value }) => (
                    <PeriodsSelect
                        compact
                        budgetLineStartDate={String(value || '')}
                        budgetLineEndDate={null}
                        isStartDate
                        hasInvertedColors
                        updateBudgetClassification={(dict: { startDate?: string }) => {
                            onChange({
                                target: { value: dict.startDate ?? '' },
                            } as ChangeEvent<HTMLInputElement>);
                        }}
                        cashFlowPeriods={cashFlowPeriods || []}
                        saleDate={proForma?.saleDate || ''}
                        disabled={!operatingInfo?.operationStartDate || !proForma?.saleDate}
                        operationStartDate={operatingInfo?.operationStartDate || ''}
                    />
                ),
            };
        }

        return [...columns, ...createPeriodColumns(estimateRecoveryPeriods)];
    }, [cashFlowPeriods, proForma?.saleDate, operatingInfo?.operationStartDate, estimateRecoveryPeriods]);

    const recoveryColumns = useMemo(() => {
        const columns = [...BASE_ACTUAL_RECOVERY_COLUMNS];
        const recStartColumn = columns.find(col => col.key === 'recoveryStart');

        if (recStartColumn) {
            recStartColumn.customCell = {
                shouldShowCustomCell: () => true,
                renderCell: ({ onChange, row }) => {
                    const recovery = incomeSummary?.tenantRecoveries?.find(r => r?.id && String(r.id) === row?.id) || null;
                    return (
                        <div>
                            <PeriodsSelect
                                compact
                                hasInvertedColors
                                isStartDate
                                budgetLineEndDate={null}
                                budgetLineStartDate={recovery?.recoveryStart || ''}
                                updateBudgetClassification={(dict: { startDate?: string }) => {
                                    onChange({
                                        target: { value: dict.startDate ?? '' },
                                    } as ChangeEvent<HTMLInputElement>);
                                }}
                                cashFlowPeriods={cashFlowPeriods || []}
                                saleDate={proForma?.saleDate || ''}
                                disabled={!operatingInfo?.operationStartDate || !proForma?.saleDate}
                                operationStartDate={operatingInfo?.operationStartDate || ''}
                            />
                        </div>
                    );
                },
            };
        }

        return [...columns, ...createPeriodColumns(recoveryPeriods)];
    }, [cashFlowPeriods, proForma?.saleDate, operatingInfo?.operationStartDate, incomeSummary?.tenantRecoveries, recoveryPeriods]);

    const otherIncomePeriods = useMemo(() => getOtherIncomePeriods(
        cashFlowPeriods,
        operatingPeriodCashFlows,
        incomeSummary?.otherIncome || [],
        false,
    ), [cashFlowPeriods, operatingPeriodCashFlows, incomeSummary?.otherIncome, operatingInfo?.operationBeginMonth]);

    const otherIncomeColumns = useMemo(() => {
        const columns = [...OTHER_INCOME_BASE_COLUMNS];
        const startColumn = columns.find(col => col.key === 'startDate');
        const pricePerSFperYearColumn = columns.find(col => col.key === 'pricePerSfPerYear');

        if (pricePerSFperYearColumn) {
            pricePerSFperYearColumn.header = isIncomeCalculationTypeTenants ? '$/RSF/year' : '$/SF/year';
        }

        if (startColumn) {
            startColumn.format = 'none';
            startColumn.type = 'text';
            startColumn.isReadOnly = true;
            startColumn.customCell = {
                shouldShowCustomCell: () => true,
                renderCell: ({ onChange, row }) => {
                    const income = incomeSummary?.otherIncome?.find(inc => String(inc?.id) === row.id);
                    return (
                        <div>
                            <PeriodsSelect
                                compact
                                hasInvertedColors
                                isStartDate
                                budgetLineEndDate={null}
                                budgetLineStartDate={income?.startDate || ''}
                                updateBudgetClassification={(dict: { startDate?: string }) => {
                                    onChange({
                                        target: { value: dict.startDate ?? '' },
                                    } as ChangeEvent<HTMLInputElement>);
                                }}
                                cashFlowPeriods={cashFlowPeriods || []}
                                saleDate={proForma?.saleDate || ''}
                                disabled={!operatingInfo?.operationStartDate || !proForma?.saleDate}
                                operationStartDate={operatingInfo?.operationStartDate || ''}
                            />
                        </div>
                    );
                },
            };
        }

        return [...columns, ...createPeriodColumns(otherIncomePeriods)];
    }, [cashFlowPeriods, proForma?.saleDate, operatingInfo?.operationStartDate, operatingInfo?.incomeCalculationType, otherIncomePeriods, incomeSummary?.otherIncome]);

    const totalPotentialColumns = useMemo(() => {
        const baseCols = TOTAL_POTENTIAL_BASE_ACTUALS_COLUMNS.map((col: ProFormaGenericColumn) => {
            let { header } = col;
            if (col.key === 'valuePerSfPerYear') {
                header = formatCurrencyWithParentheses(Number(incomeSummary?.totalPotentialIncome?.totalPricePerSf || 0),
                    shouldShowCents(col.key, showCents));
            } else if (col.key === 'amountPerYear') {
                header = formatCurrencyWithParentheses(Number(incomeSummary?.totalPotentialIncome?.totalAmountPerYear || 0),
                    shouldShowCents(col.key, showCents));
            }
            return { ...col, header };
        });

        const periodColumns = cashFlowPeriods.map((periodStartDate, index) => {
            const cashFlow = operatingPeriodCashFlows[index];
            return {
                key: `column_${periodStartDate}`,
                field: `column_${periodStartDate}`,
                header: (
                    <div className="text-right">
                        {formatCurrencyWithParentheses(Number(cashFlow?.totalPotentialIncome || 0), shouldShowCents(`column_${periodStartDate}`, showCents))}
                    </div>
                ),
                format: 'currency' as const,
                isFixed: false,
            };
        });

        return [...baseCols, ...periodColumns];
    }, [incomeSummary?.totalPotentialIncome, cashFlowPeriods, operatingPeriodCashFlows, showCents]);

    useEffect(() => {
        if (incomeSummary) {
            if (incomeSummary.incomeTenants) {
                setRentalRows(() => (incomeSummary.incomeTenants || []).map(tenant => {
                    // Get the period values for this tenant
                    const periodValues = Object.fromEntries(
                        tenantPeriods.map(period => [
                            `column_${period.periodDate}`,
                            period.values[tenant?.tenant || ''] || '-',
                        ]),
                    );

                    return {
                        id: String(tenant?.id),
                        suite: tenant?.suite || '',
                        tenant: tenant?.tenant || '',
                        leaseStart: '', // TODO: add initial value to the periodSelect, not here
                        rentStart: '', // TODO: add initial value to the periodSelect, not here
                        rentStep: tenant?.rentStep || '',
                        rentGrowth: tenant?.rentGrowth || 0,
                        squareFootage: tenant.squareFootage || '',
                        valuePerSfPerYear: tenant.valuePerSfPerYear || '',
                        annualRent: tenant.annualRent || '',
                        ...periodValues, // Add the period values to the row
                    };
                }));

                if (incomeSummary.tenantRecoveries) {
                    setRecoveryRows(() => {
                        const validRecoveries = (incomeSummary.tenantRecoveries || []).filter(recovery => recovery !== null);
                        const rows = validRecoveries.map(recovery => {
                            const periodValues = Object.fromEntries(
                                recoveryPeriods.map(period => [
                                    `column_${period.periodDate}`,
                                    period.values[recovery?.tenant || ''] || '-',
                                ]),
                            );

                            return {
                                id: String(recovery?.id || recovery?.tenant), // Ensure we always have an id
                                tenant: recovery?.tenant || '',
                                suite: recovery?.suite || '',
                                recoveryStart: recovery?.recoveryStart || '',
                                recoveryPercentage: recovery?.recoveryPercentage || '',
                                proRataShare: recovery?.proRataShare || '-',
                                recoveryValuePerSfPerYear: recovery?.recoveryValuePerSfPerYear || '',
                                recoveryAmountPerYear: recovery?.recoveryAmountPerYear || '',
                                ...periodValues,
                            };
                        });

                        return rows;
                    });
                }
            }

            if (incomeSummary.incomeFloorEstimates) {
                setEstimateRentalRows(() => (incomeSummary.incomeFloorEstimates || []).map(estimate => {
                    // Get the period values for this floor
                    const periodValues = Object.fromEntries(
                        estimateRentalPeriods.map(period => [
                            `column_${period.periodDate}`,
                            period.values[String(estimate.floor)] || '-',
                        ]),
                    );

                    return {
                        id: String(estimate?.id),
                        floor: estimate.floor || '',
                        allocationPercentage: estimate.allocationPercentage || '',
                        sf: estimate.squareFootage !== null ? String(estimate.squareFootage) : '-',
                        valuePerSfPerYear: estimate.valuePerSfPerYear || '',
                        annualRent: estimate.annualRent || '',
                        ...periodValues, // Add the period values to the row
                    };
                }));
            }
            if (incomeSummary.recoveryEstimate !== undefined) {
                setEstimateRecoveryRows([{
                    id: '1',
                    recoveryType: 'Recovery income',
                    recoveryPercentage: incomeSummary.recoveryEstimate?.recoveryPercentage || '',
                    recoveryStart: incomeSummary.recoveryEstimate?.recoveryStart || '',
                    pricePerSf: String(incomeSummary.recoveryEstimate.valuePerSfPerYear) || '',
                    amountYear: String(incomeSummary.recoveryEstimate.amountPerYear) || '',
                    ...Object.fromEntries(
                        estimateRecoveryPeriods.map(period => [
                            `column_${period.periodDate}`,
                            period.values.value,
                        ]),
                    ),
                }]);
            }

            if (incomeSummary.otherIncome) {
                setOtherIncomeRows(() => (incomeSummary.otherIncome || []).map(income => ({
                    id: String(income?.id),
                    name: income?.name || '',
                    startDate: income?.startDate || '',
                    annualGrowth: income?.annualGrowth || '',
                    percentOfTotalRent: income?.percentOfTotalRent || '',
                    pricePerSfPerYear: income?.pricePerSfPerYear || '',
                    amountPerYear: income?.amountPerYear || '',
                    ...Object.fromEntries(
                        otherIncomePeriods.map(period => [
                            `column_${period.periodDate}`,
                            period.values[income?.name || ''] || '-',
                        ]),
                    ),
                })));
            }

            if (incomeSummary.incomeAdjustments) {
                setIncomeAdjustmentsRows(() => (incomeSummary.incomeAdjustments || []).map(adjustment => ({
                    id: String(adjustment?.id),
                    name: adjustment?.name || '',
                    percentOfIncome: adjustment?.percentOfIncome || 0,
                    pricePerSfPerMonth: adjustment?.pricePerSf || 0,
                    amountPerYear: adjustment?.amountPerYear || 0,
                    adjustmentType: adjustment?.adjustmentType,
                    ...Object.fromEntries(
                        incomeAdjustmentPeriods.map(period => [
                            `column_${period.periodDate}`,
                            period.values[adjustment?.name || ''] || '-',
                        ]),
                    ),
                })));
            }
        }
    }, [incomeSummary, estimateRentalPeriods, estimateRecoveryPeriods, otherIncomePeriods, recoveryPeriods]);

    return (
        <RenderIf isTrue={proForma?.proFormaModelId === 2 || proForma?.proFormaModelId === 3 || proForma?.proFormaModelId === 4}>
            <div className="ProFormaIncome p-3">
                <RenderIf isTrue={isIncomeCalculationTypeTenants}>
                    <ProFormaGenericTable
                        numFirstColumns={2}
                        fixedColumnsMaxWidth="800px"
                        firstColumnWidth="80px"
                        secondColumnWidth="100px"
                        thirdColumnWidth="90px"
                        fourthColumnWidth="90px"
                        fifthColumnWidth="70px"
                        sixthColumnWidth="120px"
                        seventhColumnWidth="65px"
                        rows={rentalRows}
                        setRows={setRentalRows}
                        columns={rentalColumns}
                        onScrollableRef={createRentalRefHandler(rentalTableRef)}
                        onScroll={() => handleRentalTableScroll('table')}
                        handleUpdateRow={(rowId, dataKey) => handleUpdateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.TENANT.UPDATE, rowId, dataKey)}
                        handleDeleteRow={(rowId: number) => handleDeleteOperationsRow(
                            OPERATIONS_INCOME_WS_HANDLERS.TENANT.DELETE,
                            rowId,
                        )}
                        handleAddRow={() => handleCreateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.TENANT.CREATE, { suite: 'Suite', tenant: 'New tenant' })}
                        emptyRowText="Your added tenants will appear here"
                        emptyRowTitleText="Tenant"
                        totalText="Total rental income"
                        totalValues={incomeSummary?.totalRentalIncome ? {
                            tenant: false,
                            leaseStart: false,
                            rentStart: false,
                            rentStep: false,
                            rentGrowth: false,
                            squareFootage: String(incomeSummary.totalRentalIncome.totalSquareFootage || 0),
                            valuePerSfPerYear: String(incomeSummary.totalRentalIncome.totalValuePerSfPerYear || 0),
                            annualRent: String(incomeSummary.totalRentalIncome.totalAnnualRent || 0),
                            ...Object.fromEntries(
                                tenantPeriods.map(period => {
                                    const periodIndex = cashFlowPeriods.findIndex(p => p === period.periodDate);
                                    const cashFlow = periodIndex !== -1 ? operatingPeriodCashFlows[periodIndex] : null;
                                    return [
                                        `column_${period.periodDate}`,
                                        cashFlow?.totalFloorEstimatesIncome !== null && cashFlow?.totalFloorEstimatesIncome !== undefined
                                            ? String(cashFlow.totalFloorEstimatesIncome)
                                            : '0',
                                    ];
                                }),
                            ),
                        } : undefined}
                    />
                    <div className="pb-1" />
                    <ProFormaGenericTable
                        hideAddButton
                        fixedColumnsMaxWidth="800px"
                        firstColumnWidth="150px"
                        secondColumnWidth="60px"
                        thirdColumnWidth="60px"
                        rows={recoveryRows}
                        setRows={setRecoveryRows}
                        columns={recoveryColumns}
                        onScroll={() => handleRecoveryTableScroll('table')}
                        onScrollableRef={createRecoveryRefHandler(recoveryTableRef)}
                        handleUpdateRow={(rowId, dataKey) => handleUpdateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.TENANT.UPDATE, rowId, dataKey)}
                        handleDeleteRow={(rowId: number) => handleDeleteOperationsRow(
                            OPERATIONS_INCOME_WS_HANDLERS.TENANT.DELETE,
                            rowId,
                        )}
                        emptyRowText="Your added tenants will appear here"
                        emptyRowTitleText="Tenant"
                        totalText="Total recovery income"
                        totalValues={incomeSummary?.totalRecoveryIncome ? {
                            recoveryStart: false,
                            recoveryPercentage: false,
                            calculatedOn: false,
                            proRataShare: false,
                            recoveryValuePerSfPerYear: String(incomeSummary.totalRecoveryIncome.totalValuePerSfPerYear || 0),
                            recoveryAmountPerYear: String(incomeSummary.totalRecoveryIncome.totalAmountPerYear || 0),
                            ...Object.fromEntries(
                                recoveryPeriods.map(period => {
                                    const periodIndex = cashFlowPeriods.findIndex(p => p === period.periodDate);
                                    const cashFlow = periodIndex !== -1 ? operatingPeriodCashFlows[periodIndex] : null;
                                    return [
                                        `column_${period.periodDate}`,
                                        cashFlow?.totalRecoveryIncome !== null && cashFlow?.totalRecoveryIncome !== undefined
                                            ? String(cashFlow.totalRecoveryIncome)
                                            : '0',
                                    ];
                                }),
                            ),
                        } : undefined}
                    />
                    <div className="pb-1" />
                </RenderIf>
                <RenderIf isTrue={!isIncomeCalculationTypeTenants}>
                    <ProFormaGenericTable
                        fixedColumnsMaxWidth="800px"
                        numFirstColumns={2}
                        firstColumnWidth="100px"
                        secondColumnWidth="200px"
                        rows={estimateRentalRows}
                        setRows={setEstimateRentalRows}
                        columns={rentalEstimateColumns}
                        onScroll={() => handleRentalTableScroll('table')}
                        onScrollableRef={createRentalRefHandler(rentalTableRef)}
                        handleUpdateRow={(rowId: number, dataKey: string) => handleUpdateOperationsRow(
                            OPERATIONS_INCOME_WS_HANDLERS.ESTIMATE.UPDATE,
                            rowId,
                            dataKey,
                        )}
                        handleDeleteRow={(rowId: number) => handleDeleteOperationsRow(
                            OPERATIONS_INCOME_WS_HANDLERS.ESTIMATE.DELETE,
                            rowId,
                        )}
                        handleAddRow={() => handleCreateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.ESTIMATE.CREATE)}
                        disableAddButton={incompleteAllocationFloors.length === 0 && hasAllFloors}
                        disabledPlusIconTooltipMessage="Add stories from general information or reduce a floor allocation."
                        emptyRowText="Your floor allocations will appear here"
                        emptyRowTitleText="Allocation"
                        totalText="Total rental income"
                        totalValues={incomeSummary?.incomeFloorEstimates ? {
                            allocationPercentage: false,
                            sf: String(incomeSummary.totalRentalIncome.totalSquareFootage || 0),
                            valuePerSfPerYear: String(incomeSummary?.totalRecoveryIncome?.totalValuePerSfPerYear || 0),
                            annualRent: String(incomeSummary?.totalRecoveryIncome?.totalAmountPerYear || 0),
                            ...Object.fromEntries(
                                estimateRentalPeriods.map(period => {
                                    const periodIndex = cashFlowPeriods.findIndex(p => p === period.periodDate);
                                    const cashFlow = periodIndex !== -1 ? operatingPeriodCashFlows[periodIndex] : null;
                                    return [
                                        `column_${period.periodDate}`,
                                        cashFlow?.recoveryEstimate !== null && cashFlow?.totalFloorEstimatesIncome !== undefined
                                            ? String(cashFlow.totalFloorEstimatesIncome)
                                            : '0',
                                    ];
                                }),
                            ),
                        } : undefined}
                    />
                    <div className="pb-1" />
                    <ProFormaGenericTable
                        fixedColumnsMaxWidth="800px"
                        firstColumnWidth="320px"
                        hideAddButton
                        rows={estimateRecoveryRows}
                        setRows={setEstimateRecoveryRows}
                        columns={estimateRecoveryColumns}
                        onScroll={() => handleRecoveryTableScroll('table')}
                        onScrollableRef={createRecoveryRefHandler(recoveryTableRef)}
                        handleUpdateRow={(rowId: number, dataKey: string) => handleUpdateEstimateRecovery(rowId, dataKey as keyof IPatchOperatingInfoData)}
                        emptyRowText="Your added recoveries will appear here"
                        totalText="Total recovery income"
                        totalValues={{
                            recoveryStart: false,
                            recoveryPercentage: false,
                            pricePerSf: String(operatingInfo?.recoveryPercentage || 0),
                            amountYear: String(operatingInfo?.recoveryPercentage || 0),
                            ...Object.fromEntries(
                                estimateRecoveryPeriods.map(period => {
                                    const periodIndex = cashFlowPeriods.findIndex(p => p === period.periodDate);
                                    const cashFlow = periodIndex !== -1 ? operatingPeriodCashFlows[periodIndex] : null;
                                    return [
                                        `column_${period.periodDate}`,
                                        cashFlow?.totalRecoveryIncome !== null && cashFlow?.totalRecoveryIncome !== undefined
                                            ? String(cashFlow.totalRecoveryIncome)
                                            : '0',
                                    ];
                                }),
                            ),
                        }}
                    />
                    <div className="pb-1" />
                </RenderIf>
                <ProFormaGenericTable
                    fixedColumnsMaxWidth="800px"
                    firstColumnWidth="150px"
                    secondColumnWidth="60px"
                    fourthColumnWidth="100px"
                    columns={otherIncomeColumns}
                    rows={otherIncomeRows}
                    setRows={setOtherIncomeRows}
                    handleAddRow={() => handleCreateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.OTHER_INCOME.CREATE)}
                    handleDeleteRow={rowId => handleDeleteOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.OTHER_INCOME.DELETE, rowId)}
                    handleUpdateRow={(rowId, dataKey) => handleUpdateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.OTHER_INCOME.UPDATE, rowId, dataKey)}
                    onScrollableRef={createOtherIncomeRefHandler(otherIncomeTableRef)}
                    onScroll={() => handleOtherIncomeTableScroll('table')}
                    totalText="Total other income"
                    totalValues={incomeSummary?.totalOtherIncome ? {
                        startDate: false,
                        annualGrowth: false,
                        percentOfTotalRent: false,
                        pricePerSfPerYear: String(incomeSummary.totalOtherIncome.totalPricePerSf || 0),
                        amountPerYear: String(incomeSummary.totalOtherIncome.totalAmountPerYear || 0),
                        ...Object.fromEntries(
                            otherIncomePeriods.map(period => {
                                const periodIndex = cashFlowPeriods.findIndex(p => p === period.periodDate);
                                const cashFlow = periodIndex !== -1 ? operatingPeriodCashFlows[periodIndex] : null;
                                return [
                                    `column_${period.periodDate}`,
                                    cashFlow?.totalOtherIncome !== null && cashFlow?.totalOtherIncome !== undefined
                                        ? String(cashFlow.totalOtherIncome)
                                        : '0',
                                ];
                            }),
                        ),
                    } : undefined}
                />
                <div className="pb-1" />
                <ProFormaGenericTable
                    fixedColumnsMaxWidth="800px"
                    firstColumnWidth="200px"
                    columns={totalPotentialColumns}
                    rows={[]}
                    setRows={() => { }}
                    hideTotalFooter
                    hideAddButton
                    onScrollableRef={createTotalPotentialRefHandler(totalPotentialTableRef)}
                    onScroll={() => handleTotalPotentialTableScroll('table')}
                />
                <div className="pb-1" />
                <ProFormaGenericTable
                    useLargerCells
                    fixedColumnsMaxWidth="800px"
                    firstColumnWidth="200px"
                    columns={incomeAdjustmentsColumns}
                    rows={incomeAdjustmentsRows}
                    setRows={setIncomeAdjustmentsRows}
                    handleAddRow={() => handleCreateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.INCOME_ADJUSTMENT.CREATE)}
                    handleDeleteRow={rowId => handleDeleteOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.INCOME_ADJUSTMENT.DELETE, rowId)}
                    handleUpdateRow={(rowId, dataKey) => handleUpdateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.INCOME_ADJUSTMENT.UPDATE, rowId, dataKey)}
                    onScrollableRef={createIncomeAdjustmentsRefHandler(incomeAdjustmentsTableRef)}
                    onScroll={() => handleIncomeAdjustmentsTableScroll('table')}
                    totalText="Effective gross revenue"
                    totalValues={incomeSummary?.effectiveGrossRevenue ? {
                        percentOfIncome: false,
                        pricePerSfPerMonth: String(incomeSummary.effectiveGrossRevenue.totalPricePerSf || 0),
                        amountPerYear: String(incomeSummary.effectiveGrossRevenue.totalAmountPerYear || 0),
                        ...Object.fromEntries(
                            incomeAdjustmentPeriods.map(period => {
                                const periodIndex = cashFlowPeriods.findIndex(p => p === period.periodDate);
                                const cashFlow = periodIndex !== -1 ? operatingPeriodCashFlows[periodIndex] : null;
                                return [
                                    `column_${period.periodDate}`,
                                    cashFlow?.totalIncomeAdjustments !== null && cashFlow?.totalIncomeAdjustments !== undefined
                                        ? String(cashFlow.totalIncomeAdjustments)
                                        : '0',
                                ];
                            }),
                        ),
                    } : undefined}
                />
            </div>
        </RenderIf>
    );
};

export default OfficeIndustrialRetailIncomeTable;
